import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAt,
  endAt,
  where,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { TradeItem } from "./tradeTypes";
import {
  fillMissingPricesWithScryfall,
  formatCurrency,
  toTradeItemFromOtherUserCard,
} from "./tradeHelpers";
import { useFocusTrap } from "./useFocusTrap";

type UserLite = { uid: string; username: string; displayName?: string | null };

type OtherUserCardLite = {
  id: string;
  scryfall_id?: string | null;
  name: string;
  set?: string | null;
  number?: string | null;
  image?: string | null;
  foil?: boolean;
  qty?: number | null;
  price?: number | null;
  condition?: string | null;
  for_trade?: boolean;
};

type Props = {
  open: boolean;
  onClose(): void;
  onConfirm(items: TradeItem[]): void;
  // required/preselected user (from inline trades page search). If absent, modal will prompt.
  initialUser?: { uid: string; username: string; displayName?: string | null };
};

export default function OtherCollectionPickerModal({ open, onClose, onConfirm, initialUser }: Props) {
  // no more in-modal username search. When opened with initialUser, auto-load that user's cards.
  useEffect(() => {
    if (!open || !initialUser) return;
    void (async () => {
      try {
        await pickUser(initialUser);
      } catch {
        // pickUser handles errors
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialUser]);

  // modal is always in "pick" mode when initialUser provided.
  const [stage] = useState<"pick">("pick");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // selected user (populated by initialUser)
  const [selectedUser, setSelectedUser] = useState<UserLite | null>(null);

  // Selected user's tradable cards
  const [cards, setCards] = useState<OtherUserCardLite[]>([]);
  const [qText, setQText] = useState("");
  const [selected, setSelected] = useState<Record<string, number>>({}); // cardId -> qty

  const panelRef = useRef<HTMLDivElement | null>(null);
  const userSearchRef = useRef<HTMLInputElement | null>(null);
  const itemSearchRef = useRef<HTMLInputElement | null>(null);
  useFocusTrap({
    active: open,
    containerRef: panelRef,
    initialFocusRef: itemSearchRef,
    onEscape: onClose,
  });

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      setCards([]);
      setSelected({});
      setError(null);
    }
  }, [open]);

  async function pickUser(u: UserLite) {
    setSelectedUser(u);
    setBusy(true);
    setError(null);
    setSelected({});
    try {
      // Optional: check profile for public trades flag
      try {
        const prof = await getDoc(doc(db, "users", u.uid));
        const v = prof.data() as any;
        if (v && v.trades_private === true) {
          throw new Error("This user’s trade list is private.");
        }
      } catch {
        // ignore profile absence; rely on security rules
      }

      // Load tradable cards
      const ref = collection(db, "users", u.uid, "cards");
      const snap = await getDocs(query(ref, where("for_trade", "==", true), limit(200)));
      const rows: OtherUserCardLite[] = [];
      snap.forEach((d) => {
        const v = d.data() as any;
        rows.push({
          id: d.id,
          scryfall_id: v?.scryfall_id ?? null,
          name: v?.name ?? "Unknown",
          set: v?.set ?? null,
          number: v?.number ?? null,
          image: v?.image ?? null,
          foil: Boolean(v?.foil),
          qty: Number(v?.qty ?? 1),
          price: typeof v?.price === "number" ? v.price : null,
          condition: v?.condition ?? null,
          for_trade: Boolean(v?.for_trade),
        });
      });
      setCards(rows);
    } catch (e: any) {
      setError(e?.message || "Failed to load user's tradable cards.");
      // remain or reset to empty state
      setSelectedUser(null);
    } finally {
      setBusy(false);
    }
  }

  const filtered = useMemo(() => {
    const s = qText.trim().toLowerCase();
    if (!s) return cards;
    return cards.filter((c) => {
      const setCode = (c.set || "").toLowerCase();
      return (
        c.name.toLowerCase().includes(s) ||
        setCode.includes(s) ||
        (c.number || "").toString().toLowerCase().includes(s)
      );
    });
  }, [cards, qText]);

  function toggle(id: string, maxQty: number) {
    setSelected((prev) => {
      if (prev[id]) {
        const clone = { ...prev };
        delete clone[id];
        return clone;
      }
      return { ...prev, [id]: Math.min(1, maxQty) };
    });
  }

  function setQty(id: string, qty: number, maxQty: number) {
    const q = Math.max(1, Math.min(maxQty, Math.floor(Number(qty) || 1)));
    setSelected((prev) => ({ ...prev, [id]: q }));
  }

  async function confirm() {
    if (!selectedUser) return onClose();
    const pickedIds = Object.keys(selected);
    if (pickedIds.length === 0) return onClose();
    const chosen = cards.filter((c) => pickedIds.includes(c.id));
    const items: TradeItem[] = chosen.map((c) => {
      const it = toTradeItemFromOtherUserCard(c, selectedUser.uid);
      it.qty = selected[c.id] || 1;
      return it;
    });
    const priced = await fillMissingPricesWithScryfall(items);
    onConfirm(priced);
    onClose();
  }

  if (!open) return null;

  return (
    <div
      className="fixed left-0 right-0 z-40 flex items-center justify-center"
      aria-hidden={!open}
      style={{
        // center between scoreboard bottom and bottom nav top
        top: "calc(var(--scoreboard-height, 120px) + 12px)",
        bottom: "calc(var(--app-bottom-nav-height, 72px) + 12px)",
        position: "fixed",
      }}
    >
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="other-collection-picker-title"
        className="relative z-10 w-full mx-auto rounded-md border border-gray-800 bg-gray-950 p-3 shadow-xl"
        tabIndex={-1}
        style={{
          maxWidth: "960px",
          // constrain panel to available space between scoreboard & bottom nav and allow scrolling
          maxHeight:
            "calc(100vh - (var(--scoreboard-height, 120px) + var(--app-bottom-nav-height, 72px) + 24px))",
          overflow: "auto",
        }}
      >
        <div className="mb-3 flex items-center justify-between">
          <div id="other-collection-picker-title" className="text-lg font-semibold text-gray-100">
            {selectedUser ? `Picking from @${selectedUser.username}` : "No user selected"}
          </div>
          <button className="rounded border border-gray-700 bg-black/40 px-2 py-1 text-gray-200 hover:bg-white/5" onClick={onClose} aria-label="Close picker">
            Close
          </button>
        </div>

        {!selectedUser ? (
          <div className="p-6 text-center text-sm text-gray-400">
            No user specified. Select a user on the Trades page and click "Add from User Collection" to open this picker.
          </div>
        ) : (
           <div className="space-y-3">
             <div className="flex items-center justify-between">
               <input
                 ref={itemSearchRef}
                 value={qText}
                 onChange={(e) => setQText(e.target.value)}
                 placeholder="Filter by name, set, or number"
                 className="w-full rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-100"
               />
               <button
                 className="ml-2 rounded border border-gray-700 bg-black/40 px-2 py-1 text-gray-200 hover:bg-white/5"
                 onClick={() => {
-                  setStage("find");
-                  setSelectedUser(null);
-                  setQText("");
-                  setUsers([]);
-                  setError(null);
+                  // Close picker and clear selection to return to trades
+                  onClose();
                 }}
               >
                 Back
               </button>
             </div>
 
             {error && (
               <div className="rounded border border-red-800 bg-red-900/20 p-2 text-sm text-red-200">
                 {error}
               </div>
             )}
 
             <div className="rounded border border-gray-800 bg-transparent p-0">
               {busy ? (
                 <div className="p-6 text-center text-sm text-gray-300">Loading…</div>
               ) : filtered.length === 0 ? (
                 <div className="p-6 text-center text-sm text-gray-400">No tradable items.</div>
               ) : (
                 <div className="overflow-x-auto">
                   <div className="flex gap-2 px-0 py-2">
                      {filtered.map((c) => {
                        const maxQty = Math.max(1, Number(c.qty || 1));
                        const isSelected = Boolean(selected[c.id]);
                        return (
                         <button
                           key={c.id}
                           onClick={() => toggle(c.id, maxQty)}
                           className={`flex-shrink-0 w-32 sm:w-36 p-1 rounded-md border ${
                             isSelected ? "border-amber-500 bg-amber-600/10" : "border-gray-800 bg-black/20"
                           } hover:scale-[1.01] transition`}
                           title={c.name}
                         >
                            <div className="relative h-28 w-full mb-0 flex items-center justify-center">
                              {c.image ? (
                                <img src={c.image} alt={c.name} className="h-full w-auto object-cover rounded" />
                              ) : (
                                <div className="h-24 w-full rounded bg-black/40" />
                              )}
                              {isSelected && (
                                <div className="absolute top-1 right-1 rounded bg-amber-600/90 px-2 py-0.5 text-[11px] text-black font-semibold">
                                  ✓
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
 
             <div className="mt-3 flex items-center justify-between">
               <div className="text-xs text-gray-400">
                 {selectedUser
                   ? `@${selectedUser.username} — Showing ${filtered.length} of ${cards.length} tradable items`
                   : ""}
               </div>
               <div className="flex gap-2">
                 <button
                   className="rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-200 hover:bg-white/5"
                   onClick={onClose}
                 >
                   Cancel
                 </button>
                 <button
                   className="bg-amber-500 px-3 py-2 font-medium text-black hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                   disabled={Object.keys(selected).length === 0}
                   onClick={() => void confirm()}
                 >
                   Add Selected
                 </button>
               </div>
             </div>
           </div>
        )}
       </div>
     </div>
   );
 }
